import { useEffect, useState } from 'react';
import { Outlet, NavLink } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';

const LOGO_URL = '/logo.png';
const SIDEBAR_EXPANDED = 240;
const SIDEBAR_COLLAPSED = 64;
const MOBILE_BREAKPOINT = 768;

export function Layout() {
  const { user, signOut } = useAuth();
  const [collapsed, setCollapsed] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= MOBILE_BREAKPOINT);
  const [pendingCount, setPendingCount] = useState(0);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth <= MOBILE_BREAKPOINT);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Fetch pending supply requests count (admin only)
  useEffect(() => {
    if (user?.role !== 'ADMIN') return;
    const load = () =>
      api
        .getSupplyRequests({ status: 'pending' })
        .then((d) => setPendingCount(d.total || 0))
        .catch(() => {});
    load();
    const interval = setInterval(load, 60_000);
    return () => clearInterval(interval);
  }, [user?.role]);

  // Close drawer when switching to desktop
  useEffect(() => {
    if (!isMobile) setMobileOpen(false);
  }, [isMobile]);

  const closeMobile = () => setMobileOpen(false);

  // Mobile drawer always shows labels (expanded)
  const showLabels = isMobile ? true : !collapsed;
  const w = isMobile ? SIDEBAR_EXPANDED : collapsed ? SIDEBAR_COLLAPSED : SIDEBAR_EXPANDED;

  return (
    <div
      className="layout"
      style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}
    >
      {/* ── Mobile top bar ───────────────────────────────────────── */}
      <header className="admin-topbar">
        <button
          className="admin-hamburger"
          onClick={() => setMobileOpen((o) => !o)}
          aria-label="Toggle navigation"
        >
          {mobileOpen ? '✕' : '☰'}
        </button>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <img src={LOGO_URL} alt="Blackwood Barbers" style={{ height: 28 }} />
          <span
            style={{
              fontFamily: 'var(--font-family-heading)',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: 'var(--color-accent)',
            }}
          >
            ADMIN
          </span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div
            style={{
              width: 30,
              height: 30,
              borderRadius: '50%',
              backgroundColor: 'var(--color-accent)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#1e1e1e',
              fontWeight: 700,
              fontSize: 13,
              fontFamily: 'var(--font-family-heading)',
            }}
          >
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
      </header>

      {/* ── Backdrop overlay (mobile) ─────────────────────────────── */}
      {mobileOpen && <div className="admin-overlay" onClick={closeMobile} />}

      <div style={{ display: 'flex', flex: 1, minHeight: 0 }}>
        {/* ── Sidebar wrapper ───────────────────────────────────────── */}
        <div className="admin-sidebar-wrapper" style={{ position: 'relative', flexShrink: 0 }}>
          <aside
            className={`admin-sidebar${mobileOpen ? ' mobile-open' : ''}`}
            style={{
              width: w,
              height: '100vh',
              position: 'sticky',
              top: 0,
              backgroundColor: '#ffffff',
              borderRight: '1px solid var(--color-border)',
              padding: showLabels ? '20px 16px' : '20px 8px',
              display: 'flex',
              flexDirection: 'column',
              transition:
                'width 0.25s cubic-bezier(.4,0,.2,1), padding 0.25s cubic-bezier(.4,0,.2,1)',
              overflow: 'hidden',
            }}
          >
            {/* Logo */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 24,
                paddingBottom: showLabels ? 16 : 12,
                borderBottom: '1px solid var(--color-border)',
                minHeight: showLabels ? 64 : 40,
              }}
            >
              {!showLabels ? (
                <span
                  style={{
                    fontFamily: 'var(--font-family-heading)',
                    fontWeight: 700,
                    fontSize: 20,
                    color: 'var(--color-text)',
                    letterSpacing: '0.04em',
                  }}
                >
                  B
                </span>
              ) : (
                <div style={{ textAlign: 'center' }}>
                  <img
                    src={LOGO_URL}
                    alt="Blackwood Barbers"
                    style={{ height: 40, display: 'block', margin: '0 auto 6px' }}
                  />
                  <span
                    style={{
                      fontSize: 10,
                      color: 'var(--color-accent)',
                      fontFamily: 'var(--font-family-heading)',
                      textTransform: 'uppercase',
                      letterSpacing: '0.18em',
                      fontWeight: 600,
                    }}
                  >
                    Admin Portal
                  </span>
                </div>
              )}
            </div>

            {/* Nav */}
            <nav style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
              <ul style={{ listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 2 }}>
                <NavItem to="/calendar" icon="📅" collapsed={!showLabels} onNavigate={closeMobile}>
                  Calendar
                </NavItem>
                <NavItem to="/reports" icon="📊" collapsed={!showLabels} onNavigate={closeMobile}>
                  Reports
                </NavItem>
                {user?.role === 'ADMIN' && (
                  <>
                    <NavItem
                      to="/health-check"
                      icon="🩺"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Health Check
                    </NavItem>
                    <NavItem
                      to="/rosters"
                      icon="🗓"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Rosters
                    </NavItem>
                    <NavItem
                      to="/ad-tracking"
                      icon="📈"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Booking Analytics
                    </NavItem>
                    <NavItem
                      to="/messaging"
                      icon="✉️"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Messaging
                    </NavItem>
                    <NavItem
                      to="/services"
                      icon="✂️"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Services
                    </NavItem>
                    <NavItem
                      to="/locations"
                      icon="📍"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Locations
                    </NavItem>
                    <NavItem
                      to="/barbers"
                      icon="💈"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Barbers
                    </NavItem>
                    <NavItem
                      to="/inventory"
                      icon="📦"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Inventory
                    </NavItem>
                    <NavItem
                      to="/expenses"
                      icon="💵"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Expenses
                    </NavItem>
                    <NavItem
                      to="/holidays"
                      icon="🏖️"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Holidays
                    </NavItem>
                    <NavItem
                      to="/payroll/run"
                      icon="💼"
                      collapsed={!showLabels}
                      onNavigate={closeMobile}
                    >
                      Payroll
                    </NavItem>
                  </>
                )}
                <NavItem
                  to="/supply-requests"
                  icon="📋"
                  collapsed={!showLabels}
                  onNavigate={closeMobile}
                  badge={pendingCount}
                >
                  Requests
                </NavItem>
                <NavItem to="/terminals" icon="📱" collapsed={!showLabels} onNavigate={closeMobile}>
                  Terminals
                </NavItem>
              </ul>
            </nav>

            {/* User section */}
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12 }}>
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: showLabels ? 'flex-start' : 'center',
                  gap: 10,
                  marginBottom: 10,
                }}
              >
                <div
                  style={{
                    width: 32,
                    height: 32,
                    minWidth: 32,
                    borderRadius: '50%',
                    backgroundColor: 'var(--color-accent)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#fff',
                    fontWeight: 700,
                    fontSize: 13,
                    fontFamily: 'var(--font-family-heading)',
                  }}
                >
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </div>
                {showLabels && (
                  <div style={{ overflow: 'hidden' }}>
                    <p
                      style={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: 'var(--color-text)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {user?.name}
                    </p>
                    <p
                      style={{
                        fontSize: 10,
                        color: 'var(--color-accent)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.04em',
                        fontWeight: 600,
                      }}
                    >
                      {user?.role}
                    </p>
                  </div>
                )}
              </div>
              <button
                onClick={signOut}
                title="Sign Out"
                className="sidebar-signout"
                style={{
                  width: '100%',
                  padding: showLabels ? '6px 12px' : '6px',
                  backgroundColor: 'transparent',
                  border: '1px solid var(--color-border)',
                  borderRadius: 6,
                  color: 'var(--color-text-muted)',
                  fontSize: 12,
                  cursor: 'pointer',
                  transition: 'border-color 0.15s, color 0.15s',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-accent)';
                  e.currentTarget.style.color = 'var(--color-accent)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = 'var(--color-border)';
                  e.currentTarget.style.color = 'var(--color-text-muted)';
                }}
              >
                {showLabels ? 'Sign Out' : '↩'}
              </button>
            </div>
          </aside>

          {/* Edge toggle — desktop only */}
          <button
            className="sidebar-toggle-btn"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{
              position: 'absolute',
              top: 28,
              right: -12,
              zIndex: 10,
              width: 24,
              height: 24,
              borderRadius: '50%',
              border: '1px solid var(--color-border)',
              backgroundColor: '#ffffff',
              color: 'var(--color-text-muted)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              cursor: 'pointer',
              fontSize: 12,
              lineHeight: 1,
              padding: 0,
              boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
              transition: 'background-color 0.15s, color 0.15s, border-color 0.15s',
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = 'var(--color-accent)';
              e.currentTarget.style.color = '#fff';
              e.currentTarget.style.borderColor = 'var(--color-accent)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#ffffff';
              e.currentTarget.style.color = 'var(--color-text-muted)';
              e.currentTarget.style.borderColor = 'var(--color-border)';
            }}
          >
            {collapsed ? '›' : '‹'}
          </button>
        </div>

        {/* Main Content */}
        <main
          className="admin-main-content"
          style={{
            flex: 1,
            padding: 'var(--spacing-8)',
            overflow: 'auto',
            backgroundColor: 'var(--color-background)',
            minWidth: 0,
          }}
        >
          <Outlet />
        </main>
      </div>
    </div>
  );
}

function NavItem({
  to,
  icon,
  children,
  collapsed,
  onNavigate,
  badge,
}: {
  to: string;
  icon: string;
  children: React.ReactNode;
  collapsed: boolean;
  onNavigate?: () => void;
  badge?: number;
}) {
  return (
    <li>
      <NavLink
        to={to}
        onClick={onNavigate}
        title={collapsed ? (typeof children === 'string' ? children : '') : undefined}
        style={({ isActive }) => ({
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: collapsed ? 0 : 10,
          padding: collapsed ? '10px 0' : '8px 12px',
          borderRadius: 6,
          backgroundColor: isActive ? 'var(--color-accent)' : 'transparent',
          color: isActive ? '#1e1e1e' : 'var(--color-text)',
          textDecoration: 'none',
          fontSize: 13,
          fontWeight: isActive ? 600 : 500,
          transition: 'background-color 0.15s, color 0.15s',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          position: 'relative',
        })}
      >
        <span
          style={{
            fontSize: 16,
            flexShrink: 0,
            width: collapsed ? 'auto' : 20,
            textAlign: 'center',
            position: 'relative',
          }}
        >
          {icon}
          {badge != null && badge > 0 && collapsed && (
            <span
              style={{
                position: 'absolute',
                top: -6,
                right: -8,
                minWidth: 16,
                height: 16,
                borderRadius: 8,
                backgroundColor: '#e53e3e',
                color: '#fff',
                fontSize: 10,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 4px',
                lineHeight: 1,
              }}
            >
              {badge > 99 ? '99+' : badge}
            </span>
          )}
        </span>
        {!collapsed && children}
        {!collapsed && badge != null && badge > 0 && (
          <span
            style={{
              marginLeft: 'auto',
              minWidth: 20,
              height: 20,
              borderRadius: 10,
              backgroundColor: '#e53e3e',
              color: '#fff',
              fontSize: 11,
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0 6px',
              lineHeight: 1,
            }}
          >
            {badge > 99 ? '99+' : badge}
          </span>
        )}
      </NavLink>
    </li>
  );
}
